var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { omit, isString, cloneDeep, get, toArray } from "lodash";
import API from "@irati/utils/services/API";
import setIfNotSet from "@irati/utils/setIfNotSet";
import client from "@irati/utils/services/apollo";
import userErrorHandler from "./errors/userErrorHandler";
import codes from "../errorCodes/User";
import endpoints from "../endpoints";
import authentication from "../../auth";
import { UserRole } from "../enums";
import updateUserMutation from "./mutations/updateUser";
import createUserMutation from "./mutations/createUser";
import getUsersQuery from "./queries/getUsers";
import getUserByEmailQuery from "./queries/getUserByEmail";
import updateUsersByEmailMutation from "./mutations/updateUsersByEmail";
var codesToIgnore = [codes.EmailAlreadyExists];
var normaliseRole = function (role) {
    var normalisedRole = role.toLowerCase().trim();
    var roles = toArray(UserRole);
    if (roles.includes(normalisedRole)) {
        return normalisedRole;
    }
    else {
        return UserRole.user;
    }
};
var buildRoles = function (role) {
    if (role && role.toLowerCase() === UserRole.admin) {
        return [UserRole.admin, UserRole.manager, UserRole.user];
    }
    else if (role && role.toLowerCase() === UserRole.manager) {
        return [UserRole.manager, UserRole.user];
    }
    return [UserRole.user];
};
export var createIdentity = function (data, role, options) {
    if (role === void 0) { role = UserRole.user; }
    if (options === void 0) { options = {}; }
    var normalisedRole = normaliseRole(role);
    var clonedData = cloneDeep(data);
    clonedData.teams = isString(clonedData.teams) ? [clonedData.teams.replace(/ /g, "-")] : clonedData.teams;
    clonedData.role = normalisedRole;
    clonedData.roles = buildRoles(normalisedRole);
    API.sentryIgnoreError(options, codesToIgnore);
    return API.post(endpoints.users(), parseUserForPut(clonedData), options)
        .then(function (res) { return res.data; })
        .catch(userErrorHandler);
};
export var createIdentityViaGraph = function (data, role, options) {
    if (role === void 0) { role = UserRole.user; }
    if (options === void 0) { options = {}; }
    var normalisedRole = normaliseRole(role);
    var clonedData = cloneDeep(data);
    clonedData.teams = isString(clonedData.teams) ? [clonedData.teams.replace(/ /g, "-")] : clonedData.teams;
    clonedData.role = normalisedRole;
    clonedData.roles = buildRoles(normalisedRole);
    API.sentryIgnoreError(options, codesToIgnore);
    var variables = { user: clonedData };
    return client
        .mutate(__assign(__assign({}, options), { mutation: createUserMutation, variables: variables }))
        .then(function (res) { return res.data; })
        .catch(userErrorHandler);
};
export var activate = function (user, role, config) {
    if (role === void 0) { role = UserRole.user; }
    var identity = parseUserForPut(user);
    identity.uuid = user.uuid;
    identity.role = role;
    identity.roles = [role];
    return service.updateIdentity(identity, config);
};
export var deactivate = function (user, config) {
    var identity = parseUserForPut(user);
    identity.uuid = user.uuid;
    identity.role = UserRole.deactivated;
    identity.roles = [];
    identity.teams = [];
    return service.updateIdentity(identity, config);
};
export var activateViaGraph = function (user, role, options) {
    if (role === void 0) { role = UserRole.user; }
    var identity = parseUserForPut(user);
    identity.uuid = user.uuid;
    identity.role = role;
    identity.roles = [role];
    return service.updateIdentityViaGraph(identity, options);
};
export var deactivateViaGraph = function (user, options) {
    var identity = parseUserForPut(user);
    identity.uuid = user.uuid;
    identity.role = UserRole.deactivated;
    identity.roles = [];
    identity.teams = [];
    return service.updateIdentityViaGraph(identity, options);
};
export var getIdentity = function (user, config) {
    if (config === void 0) { config = {}; }
    return API.get(endpoints.users() + "/" + user.uuid, config)
        .then(function (res) { return res.data; })
        .catch(userErrorHandler);
};
export var getIdentities = function (options) {
    return API.get(endpoints.users() + "/", options)
        .then(function (res) { return res.data; })
        .catch(userErrorHandler);
};
export var getUsers = function (variables, options) {
    if (options === void 0) { options = {}; }
    return client
        .query(__assign(__assign({}, options), { query: getUsersQuery, variables: variables }))
        .then(function (_a) {
        var data = _a.data;
        return data.users;
    })
        .catch(userErrorHandler);
};
export var getTotalActiveUsers = function (tenant) {
    if (tenant === void 0) { tenant = authentication.userTenant(); }
    var params = {
        limit: 1,
        role: "user",
        tenant: tenant.uuid
    };
    return getIdentities({ params: params }).then(function (data) { return data.total; });
};
export var updateIdentity = function (user, config) {
    if (config === void 0) { config = {}; }
    API.sentryIgnoreError(config, codesToIgnore);
    return API.put(endpoints.users() + "/" + user.uuid, parseUserForPut(user), user.Etag, config)
        .then(function (res) { return res.data; })
        .catch(userErrorHandler);
};
export var updateIdentityViaGraph = function (user, options) {
    API.sentryIgnoreError(options, codesToIgnore);
    var variables = {
        user: parseUserForPut(user)
    };
    return client
        .mutate(__assign(__assign({}, options), { mutation: updateUserMutation, variables: variables }))
        .then(function (_a) {
        var data = _a.data;
        return data;
    })
        .catch(userErrorHandler);
};
export var unAuthedUser = function (uuid, config) {
    if (config === void 0) { config = {}; }
    setIfNotSet(config, "unAuth", true);
    return API.get(endpoints.unAuthedUser(uuid), config)
        .then(function (res) { return res.data; })
        .catch();
};
export var getUserByEmail = function (email, tenantId) {
    if (tenantId === void 0) { tenantId = authentication.userTenant().uuid; }
    var variables = { email: email, tenantId: tenantId };
    return client.query({ query: getUserByEmailQuery, variables: variables }).then(function (res) { return res.data.getUserByEmailAndTenantId; });
};
export var updateUserByEmail = function (users, tenantId) {
    if (tenantId === void 0) { tenantId = authentication.userTenant().uuid; }
    var variables = { users: users, tenantId: tenantId };
    return client.mutate({ mutation: updateUsersByEmailMutation, variables: variables }).then(function (res) {
        return res.data.updateUsersByEmail;
    });
};
var service = {
    createIdentity: createIdentity,
    activate: activate,
    deactivate: deactivate,
    getIdentity: getIdentity,
    getIdentities: getIdentities,
    getTotalActiveUsers: getTotalActiveUsers,
    updateIdentity: updateIdentity,
    createIdentityViaGraph: createIdentityViaGraph,
    activateViaGraph: activateViaGraph,
    deactivateViaGraph: deactivateViaGraph,
    getUsers: getUsers,
    updateIdentityViaGraph: updateIdentityViaGraph,
    getUserByEmail: getUserByEmail,
    updateUserByEmail: updateUserByEmail
};
export default service;
function parseUserForPut(user) {
    if (typeof user.teams[0] !== "string") {
        if (get(user, "teams.results", false)) {
            user.teams = user.teams.results.map(function (team) { return get(team, "uuid"); });
        }
        else {
            user.teams = user.teams.map(function (team) { return get(team, "uuid"); });
        }
    }
    if (typeof user.roles[0] !== "string") {
        user.roles = user.roles.map(function (role) { return get(role, "uuid"); });
    }
    if (!user.settings) {
        user.settings = {};
    }
    if (user.teamIds) {
        user.teamIds = undefined;
    }
    return omit(user, ["load"]);
}
